import React, { useState, useEffect } from 'react';
import api from './api';

// --- DEFAULT PRICING MASTER (You can change these values!) ---
const qualityOptions = [
    { label: '-- Select --', defaultPrice: '' },
    { label: '-11 IF', defaultPrice: 420 },
    { label: '+11 IF', defaultPrice: 480 },
    { label: '-11 VS1', defaultPrice: 360 },
    { label: '+11 VS1', defaultPrice: 380 },
    { label: '-11 SI', defaultPrice: 330 },
    { label: '+11 SI', defaultPrice: 350 },
    { label: '-11 Color GH', defaultPrice: 350 },
    { label: '+11 Color GH', defaultPrice: 400 },
    { label: '-11 Color IJ', defaultPrice: 250 },
    { label: '+11 Color IJ', defaultPrice: 350 },
    { label: 'Rejection', defaultPrice: 200 }
];

const RoughEstimate = () => {
    // Generate 10 blank rows
    const getInitialRows = () => Array.from({ length: 10 }, (_, i) => ({ id: Date.now() + i, quality: '', pieces: '', price: '' }));

    // --- STATES ---
    const [estimateName, setEstimateName] = useState('');
    const [rows, setRows] = useState(getInitialRows());
    const [yieldPercent, setYieldPercent] = useState('');
    const [labor, setLabor] = useState('');
    const [usdRate, setUsdRate] = useState('');
    const [discount, setDiscount] = useState('');
    
    const [savedEstimates, setSavedEstimates] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchSavedEstimates();
    }, []);

    const fetchSavedEstimates = async () => {
        try {
            const res = await api.get('/rough-estimates');
            setSavedEstimates(res.data.data || []);
        } catch (err) { console.error("Failed to load estimates"); }
    };

    // --- ROW HANDLERS ---
    const handleRowChange = (id, field, value) => {
        setRows(rows.map(row => {
            if (row.id === id) {
                const updatedRow = { ...row, [field]: value };
                // MAGIC: If they change the Quality dropdown, auto-fill the Price box!
                if (field === 'quality') {
                    const selectedOption = qualityOptions.find(opt => opt.label === value);
                    if (selectedOption && selectedOption.defaultPrice !== '') {
                        updatedRow.price = selectedOption.defaultPrice;
                    }
                }
                return updatedRow;
            }
            return row;
        }));
    };

    const handleReset = () => {
        setEstimateName('');
        setRows(getInitialRows());
        setYieldPercent('');
        setLabor('');
        setUsdRate('');
        setDiscount('');
    };

    // --- MATH CALCULATIONS ---
    const totalPieces = rows.reduce((sum, row) => sum + (Number(row.pieces) || 0), 0);
    const totalAmount = rows.reduce((sum, row) => sum + ((Number(row.pieces) || 0) * (Number(row.price) || 0)), 0);
    const avgRate = totalPieces > 0 ? (totalAmount / totalPieces) : 0;
    const estPadtar = avgRate * (Number(yieldPercent) || 0);
    const roughCostINR = estPadtar - (Number(labor) || 0);
    const roughCostUSD = (Number(usdRate) || 0) > 0 ? (roughCostINR / Number(usdRate)) : 0;
    const finalPriceUSD = roughCostUSD - (roughCostUSD * ((Number(discount) || 0) / 100));

    // --- SAVE TO DATABASE ---
    const handleSaveEstimate = async () => {
        if (!estimateName.trim()) return alert("Please enter an Estimate Name before saving!");
        
        try {
            setLoading(true);

            // 1. CLEAN THE DATA: Remove frontend IDs, force numbers, and ignore blank rows
            const cleanedRows = rows
                .filter(r => Number(r.pieces) > 0 || Number(r.price) > 0)
                .map(r => ({
                    quality: r.quality === '-- Select --' ? '' : r.quality,
                    pieces: Number(r.pieces) || 0,
                    price: Number(r.price) || 0
                }));

            if (cleanedRows.length === 0) {
                setLoading(false);
                return alert("Please enter at least one row with Pieces and a Rate!");
            }

            // 2. BUILD THE PERFECT PAYLOAD
            const payload = {
                name: estimateName.trim(),
                rows: cleanedRows,
                yieldPercent: Number(yieldPercent) || 0,
                labor: Number(labor) || 0,
                usdRate: Number(usdRate) || 0,
                discount: Number(discount) || 0,
                finalPriceUSD: Number(finalPriceUSD) || 0 // Ensures no 'NaN' sneaks through
            };

            // 3. SEND TO SERVER
            await api.post('/rough-estimates', payload);
            alert("✅ Estimate Saved Successfully!");
            
            fetchSavedEstimates(); // Refresh list
            handleReset(); // Clear form for next entry
            
        } catch (err) {
            console.error("Save Error:", err.response || err);
            // 🎯 NEW: This will tell us EXACTLY what Mongoose is complaining about
            alert("❌ Error saving estimate: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid mt-4 mb-5 px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-primary">🧮 Rough Purchase Estimator</h3>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-danger fw-bold" onClick={handleReset}>🔄 Reset</button>
                    <button className="btn btn-success fw-bold px-4" onClick={handleSaveEstimate} disabled={loading}>
                        {loading ? 'Saving...' : '💾 Save Estimate'}
                    </button>
                </div>
            </div>

            <div className="row">
                {/* --- LEFT SIDE: THE PARCEL BREAKDOWN --- */}
                <div className="col-lg-7 mb-4">
                    <div className="card shadow-sm border-primary mb-4">
                        <div className="card-header bg-primary text-white fw-bold d-flex align-items-center gap-2">
                            <span>📦 Parcel Name:</span>
                            <input type="text" className="form-control form-control-sm w-50 fw-bold" placeholder="e.g. Aum Star Lot 5" 
                                value={estimateName} onChange={(e) => setEstimateName(e.target.value)} />
                        </div>
                        <div className="card-body p-0">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3" style={{width: '30%'}}>Clarity / Quality</th>
                                        <th style={{width: '25%'}}>Weight / Pcs</th>
                                        <th style={{width: '25%'}}>Rate (₹)</th>
                                        <th className="text-end pe-3">Amount (₹)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => {
                                        const amount = (Number(row.pieces) || 0) * (Number(row.price) || 0);
                                        return (
                                            <tr key={row.id}>
                                                <td className="ps-3">
                                                    <select className="form-select form-select-sm border-secondary" 
                                                        value={row.quality} onChange={(e) => handleRowChange(row.id, 'quality', e.target.value)}>
                                                        {qualityOptions.map((opt, i) => (
                                                            <option key={i} value={opt.label}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input type="number" className="form-control form-control-sm text-center fw-bold" placeholder="0" 
                                                        value={row.pieces} onChange={(e) => handleRowChange(row.id, 'pieces', e.target.value)} />
                                                </td>
                                                <td>
                                                    {/* Editable Price Field */}
                                                    <input type="number" className="form-control form-control-sm text-center fw-bold text-primary" placeholder="0" 
                                                        value={row.price} onChange={(e) => handleRowChange(row.id, 'price', e.target.value)} />
                                                </td>
                                                <td className="text-end pe-3 fw-bold text-secondary">
                                                    {amount.toLocaleString()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="table-light fw-bold border-top">
                                    <tr>
                                        <td className="text-end pe-3">TOTALS:</td>
                                        <td className="text-center fs-5 text-dark">{totalPieces.toLocaleString()}</td>
                                        <td colSpan="2" className="text-end pe-3 fs-5 text-success">₹ {totalAmount.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan="4" className="text-end pe-3 text-primary pb-3">
                                            Average Rate: <span className="fs-4 ms-2">₹ {avgRate.toFixed(2)}</span>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                {/* --- RIGHT SIDE: MATH ENGINE & SAVED LIST --- */}
                <div className="col-lg-5">
                    
                    {/* The Calculator */}
                    <div className="card shadow-sm border-dark mb-4">
                        <div className="card-header bg-dark text-white fw-bold">⚙️ Padtar Parameters</div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-6">
                                    <label className="small fw-bold text-muted">Yield (%)</label>
                                    <input type="number" className="form-control border-info fw-bold text-center" placeholder="40%" 
                                        value={yieldPercent} onChange={(e) => setYieldPercent(e.target.value)} />
                                </div>
                                <div className="col-6">
                                    <label className="small fw-bold text-muted">Labor (₹)</label>
                                    <input type="number" className="form-control border-danger fw-bold text-center" placeholder="₹2500" 
                                        value={labor} onChange={(e) => setLabor(e.target.value)} />
                                </div>
                                <div className="col-6">
                                    <label className="small fw-bold text-muted">USD Rate ($)</label>
                                    <input type="number" className="form-control border-success fw-bold text-center" placeholder="₹93" 
                                        value={usdRate} onChange={(e) => setUsdRate(e.target.value)} />
                                </div>
                                <div className="col-6">
                                    <label className="small fw-bold text-muted">Discount (%)</label>
                                    <input type="number" className="form-control border-warning fw-bold text-center" placeholder="6%" 
                                        value={discount} onChange={(e) => setDiscount(e.target.value)} />
                                </div>
                            </div>
                        </div>
                        <div className="card-footer bg-light p-4">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Est. Rough Padtar:</span>
                                <span className="fw-bold">₹{estPadtar.toLocaleString()}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 border-bottom pb-3">
                                <span className="text-muted">Rough Cost (INR):</span>
                                <span className="fw-bold">₹{roughCostINR.toLocaleString()}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-success fs-5">Final Target ($):</span>
                                <span className="fw-bold text-success" style={{fontSize: '2.5rem', lineHeight: '1'}}>
                                    ${finalPriceUSD.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Saved Estimates History */}
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-secondary text-white fw-bold">
                            🕒 Recent Saved Estimates
                        </div>
                        <ul className="list-group list-group-flush" style={{maxHeight: '300px', overflowY: 'auto'}}>
                            {savedEstimates.length === 0 ? (
                                <li className="list-group-item text-muted text-center py-3">No saved estimates yet.</li>
                            ) : (
                                savedEstimates.map(est => (
                                    <li key={est._id} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="fw-bold text-primary">{est.name}</div>
                                            <div className="small text-muted">{new Date(est.createdAt).toLocaleDateString()}</div>
                                        </div>
                                        <div className="text-end">
                                            <div className="fw-bold text-success">${Number(est.finalPriceUSD).toFixed(2)}</div>
                                            <div className="badge bg-light text-dark border">{est.yieldPercent}% Yield</div>
                                        </div>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default RoughEstimate;